import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ArrowLeft, Loader2, FileText, IndianRupee, Receipt,
  CheckCircle2, Package, Wrench, Building2, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoiceGenerate = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { theme } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [garageProfile, setGarageProfile] = useState(null);
  const [isGstInvoice, setIsGstInvoice] = useState(true);
  const [approvedItems, setApprovedItems] = useState([]);
  const [totals, setTotals] = useState({
    subtotalParts: 0,
    subtotalLabour: 0,
    cgst: 0,
    sgst: 0,
    grandTotal: 0
  });

  useEffect(() => {
    fetchJobData();
    fetchGarageProfile();
  }, [jobId]);

  useEffect(() => {
    // Recalculate totals based on estimate's GST setting
    if (approvedItems.length > 0 && job?.estimate) {
      const estimateIsGst = job.estimate.is_gst_invoice !== false;
      setIsGstInvoice(estimateIsGst);
      calculateTotals(approvedItems, estimateIsGst);
    }
  }, [approvedItems, job?.estimate]);

  const fetchJobData = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setJob(data.job);
        
        // Check if invoice already exists
        if (data.job.invoice_id) {
          navigate(`/garage/jobs/${jobId}/invoice/${data.job.invoice_id}`);
          return;
        }
        
        // Get approved items from estimate
        const estimate = data.job.estimate || {};
        const approved = (estimate.line_items || []).filter(
          item => item.status === 'approved'
        );
        setApprovedItems(approved);
        
        // Use GST setting from estimate
        const estimateIsGst = estimate.is_gst_invoice !== false;
        setIsGstInvoice(estimateIsGst);
        calculateTotals(approved, estimateIsGst);
      }
    } catch (error) {
      toast.error('Failed to load job data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGarageProfile = async () => {
    const token = localStorage.getItem('garage_token');
    try {
      const response = await fetch(`${API_URL}/api/garage/dashboard/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setGarageProfile(data.profile);
        // Default to GST invoice if garage has GST number
        setIsGstInvoice(!!data.profile?.gst_number);
      }
    } catch (error) {
      console.error('Failed to fetch garage profile');
    }
  };

  const calculateTotals = (items, applyGst) => {
    if (applyGst) {
      // GST Invoice: Use base amounts from line items
      const subtotalParts = items
        .filter(i => i.item_type === 'part')
        .reduce((sum, i) => sum + (i.base_amount || i.total_amount / 1.18), 0);
      
      const subtotalLabour = items
        .filter(i => i.item_type === 'labour' || i.item_type === 'body_shop_labour')
        .reduce((sum, i) => sum + (i.base_amount || i.unit_price * i.quantity), 0);
      
      // Get GST from line items
      const partsGst = items
        .filter(i => i.item_type === 'part')
        .reduce((sum, i) => sum + (i.gst_amount || 0), 0);
      
      const labourGst = items
        .filter(i => i.item_type === 'labour' || i.item_type === 'body_shop_labour')
        .reduce((sum, i) => sum + (i.gst_amount || (i.unit_price * i.quantity * 0.18)), 0);
      
      const totalGst = partsGst + labourGst;
      const cgst = totalGst / 2;
      const sgst = totalGst / 2;
      
      const grandTotal = subtotalParts + subtotalLabour + totalGst;
      
      setTotals({
        subtotalParts,
        subtotalLabour,
        cgst,
        sgst,
        grandTotal
      });
    } else {
      // Non-GST Invoice: Simple totals
      const subtotalParts = items
        .filter(i => i.item_type === 'part')
        .reduce((sum, i) => sum + (i.total_amount || 0), 0);
      
      const subtotalLabour = items
        .filter(i => i.item_type === 'labour' || i.item_type === 'body_shop_labour')
        .reduce((sum, i) => sum + (i.total_amount || i.unit_price * i.quantity), 0);
      
      const grandTotal = subtotalParts + subtotalLabour;
      
      setTotals({
        subtotalParts,
        subtotalLabour,
        cgst: 0,
        sgst: 0,
        grandTotal
      });
    }
  };

  const handleGenerateInvoice = async () => {
    setGenerating(true);
    const token = localStorage.getItem('garage_token');
    
    try {
      // Invoice generation now uses is_gst_invoice from the estimate
      const response = await fetch(
        `${API_URL}/api/garage/invoices/${jobId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || 'Invoice generated successfully!');
        navigate(`/garage/jobs/${jobId}/invoice/${data.invoice.id}`);
      } else {
        toast.error(data.detail || 'Failed to generate invoice');
      }
    } catch (error) {
      toast.error('Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <Card className={`${theme.cardBg} border ${theme.border} p-8 text-center`}>
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <p className={theme.textPrimary}>Job not found</p>
        </Card>
      </div>
    );
  }

  const hasGstNumber = !!garageProfile?.gst_number;
  const partsCount = approvedItems.filter(i => i.item_type === 'part').length;
  const labourCount = approvedItems.filter(i => i.item_type === 'labour' || i.item_type === 'body_shop_labour').length;

  return (
    <div data-testid="invoice-generate-page" className={`min-h-screen ${theme.pageBg} transition-colors duration-200`}>
      {/* Header */}
      <header className={`${theme.cardBg} border-b ${theme.border} px-4 py-3`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(`/garage/jobs/${jobId}`)}
              className={theme.textSecondary}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className={`text-lg font-bold ${theme.textPrimary} flex items-center gap-2`}>
                <Receipt className="w-5 h-5 text-green-500" />
                Generate Invoice
              </h1>
              <p className={`text-xs ${theme.textSecondary}`}>{job.job_number}</p>
            </div>
          </div>
          <Badge 
            className={isGstInvoice && hasGstNumber ? 'bg-blue-600' : 'bg-slate-600'}
          >
            {isGstInvoice && hasGstNumber ? 'GST Invoice' : 'Non-GST Invoice'}
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Vehicle Info */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full ${theme.infoBg} flex items-center justify-center`}>
                <FileText className="w-6 h-6 text-blue-500" />
              </div>
              <div className="flex-1">
                <p className={`font-medium ${theme.textPrimary}`}>
                  {job.vehicle_make} {job.vehicle_model}
                </p>
                <p className={`text-sm ${theme.textSecondary}`}>{job.vehicle_number}</p>
              </div>
              <div className="text-right">
                <p className={`text-sm ${theme.textSecondary}`}>Customer</p>
                <p className={`font-medium ${theme.textPrimary}`}>{job.customer_name}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* GST Invoice Status - Read Only (Set at Estimate Stage) */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className={`w-5 h-5 ${isGstInvoice ? 'text-green-500' : 'text-slate-400'}`} />
                <div>
                  <p className={`font-medium ${theme.textPrimary}`}>
                    Invoice Type
                  </p>
                  <p className={`text-sm ${theme.textSecondary}`}>
                    {hasGstNumber 
                      ? `GSTIN: ${garageProfile.gst_number}` 
                      : 'No GST number registered'}
                  </p>
                </div>
              </div>
              <Badge 
                className={isGstInvoice ? 'bg-blue-600 text-white' : 'bg-slate-600 text-white'}
              >
                {isGstInvoice ? 'GST Invoice' : 'Non-GST Invoice'}
              </Badge>
            </div>
            <div className={`mt-3 p-3 rounded-lg ${
              isGstInvoice 
                ? 'bg-blue-500/10 border border-blue-500/30' 
                : 'bg-slate-700/50'
            }`}>
              <p className={`text-sm ${isGstInvoice ? 'text-blue-300' : 'text-slate-400'}`}>
                Invoice type was set during the Estimate stage. 
                {!isGstInvoice && ' This will be a Non-GST invoice with no tax breakdown.'}
                {isGstInvoice && ' GST breakdown will be included.'}
                {isGstInvoice && !hasGstNumber && ' (GSTIN not set - add in Garage Profile)'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Approved Items */}
        <Card className={`${theme.cardBg} border ${theme.border}`}>
          <CardHeader className="py-3">
            <CardTitle className={`text-sm ${theme.textPrimary} flex items-center gap-2`}>
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              Approved Items ({approvedItems.length})
            </CardTitle>
          </CardHeader>
          <div className={`border-t ${theme.border}`}>
            {/* Parts Section */}
            {partsCount > 0 && (
              <div className="p-4 border-b border-slate-700/50">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-blue-500" />
                  <span className={`text-sm font-medium ${theme.textPrimary}`}>
                    Parts ({partsCount})
                  </span>
                </div>
                <div className="space-y-2">
                  {approvedItems.filter(i => i.item_type === 'part').map((item, idx) => (
                    <div key={item.id || idx} className="flex justify-between items-center">
                      <span className={`text-sm ${theme.textSecondary}`}>
                        {item.part_name || item.description}
                        {item.quantity > 1 && ` × ${item.quantity}`}
                      </span>
                      <span className={`text-sm font-medium ${theme.textPrimary}`}>
                        ₹{item.total_amount?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Labour Section */}
            {labourCount > 0 && (
              <div className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Wrench className="w-4 h-4 text-orange-500" />
                  <span className={`text-sm font-medium ${theme.textPrimary}`}>
                    Labour ({labourCount})
                  </span>
                </div>
                <div className="space-y-2">
                  {approvedItems.filter(i => i.item_type === 'labour' || i.item_type === 'body_shop_labour').map((item, idx) => (
                    <div key={item.id || idx} className="flex justify-between items-center">
                      <span className={`text-sm ${theme.textSecondary}`}>
                        {item.description || item.part_name}
                        {item.item_type === 'body_shop_labour' && <span className="ml-1 text-purple-500">(Body Shop)</span>}
                      </span>
                      <span className={`text-sm font-medium ${theme.textPrimary}`}>
                        ₹{(item.unit_price * item.quantity).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Totals */}
        <Card className={`bg-gradient-to-r ${
          isGstInvoice && hasGstNumber 
            ? 'from-blue-500/10 to-indigo-500/10 border-blue-500/30' 
            : 'from-orange-500/10 to-red-500/10 border-orange-500/30'
        }`}>
          <CardContent className="p-4">
            <div className="space-y-2 max-w-xs ml-auto">
              <div className="flex justify-between text-sm">
                <span className={theme.textSecondary}>Parts Subtotal</span>
                <span className={theme.textPrimary}>₹{totals.subtotalParts.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className={theme.textSecondary}>Labour Subtotal</span>
                <span className={theme.textPrimary}>₹{totals.subtotalLabour.toLocaleString()}</span>
              </div>
              {isGstInvoice && hasGstNumber && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>CGST (9%)</span>
                    <span className={theme.textPrimary}>₹{totals.cgst.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className={theme.textSecondary}>SGST (9%)</span>
                    <span className={theme.textPrimary}>₹{totals.sgst.toLocaleString()}</span>
                  </div>
                </>
              )}
              <div className={`flex justify-between text-xl font-bold border-t ${
                isGstInvoice && hasGstNumber ? 'border-blue-500/30' : 'border-orange-500/30'
              } pt-3 mt-3`}>
                <span className={theme.textPrimary}>Grand Total</span>
                <span className={isGstInvoice && hasGstNumber ? 'text-blue-400' : 'text-orange-400'}>
                  ₹{totals.grandTotal.toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generate Button */}
        <Button
          onClick={handleGenerateInvoice}
          disabled={generating || approvedItems.length === 0}
          className={`w-full py-6 text-lg ${
            isGstInvoice && hasGstNumber
              ? 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
              : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
          }`}
          data-testid="generate-invoice-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Receipt className="w-5 h-5 mr-2" />
              Generate {isGstInvoice && hasGstNumber ? 'GST' : 'Non-GST'} Invoice
            </>
          )}
        </Button>

        {approvedItems.length === 0 && (
          <div className={`text-center p-4 rounded-lg ${theme.warningBg}`}>
            <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
            <p className={`text-sm ${theme.textSecondary}`}>
              No approved items found. Customer must approve items in the estimate first.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default InvoiceGenerate;
